import { Breadcrumb, Button, Card, Spin, Input, List } from 'antd';
import { SearchOutlined, EyeTwoTone, DeleteTwoTone } from '@ant-design/icons';
import { capitalize } from 'lodash';
import { useContext, useState, useMemo } from 'react';
import gql from 'graphql-tag';
import ErrorMessage from '../ErrorMessage';
import ClassAvatar from '../images/ClassAvatar';
import LabeledInput from '../ui/LabeledInput';
import GradePicker from '../parameters/GradePicker';
import AgeGroupPicker from '../parameters/AgeGroupPicker';
import AssignmentsContext from '../../lib/AssignmentsContext';
import { useQuery, useReportsAccess } from '../../lib/hooks';
import styles from './EntityPicker.module.scss';
const mixedEntitiesSorter = (a, b) => {
  const typenameOrder = ['State', 'District', 'School', 'Class', 'User'];
  const typeOrder = ['DISTRICT', 'PARTNER', 'COMMUNITY'];
  return typenameOrder.indexOf(a.__typename) - typenameOrder.indexOf(b.__typename) || (a.name === b.name ? 0 : a.name < b.name ? -1 : 1) || typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
};
const descFor = e => {
  switch (e.__typename) {
    case 'District':
      return `${capitalize(e.type)} ${e.type === 'COMMUNITY' ? '' : 'Schools'}`.trim();
    case 'School':
      return `${capitalize(e.type)} School`;
    case 'Class':
      return `${capitalize(e.type)} Class`;
    default:
      return e.__typename;
  }
};
const nameFor = e => e.__typename === 'Class' ? `${e.name} (${e.semester}, ${e.grade})` : e.name;
const avatarFor = e => e.__typename === 'Class' ? <ClassAvatar of={e} /> : null;
const displayEntities = (arr, actions) => <List itemLayout="horizontal" dataSource={[...arr].sort(mixedEntitiesSorter)} style={{
  maxHeight: '300px',
  overflow: 'auto'
}} renderItem={e => <List.Item actions={actions.map(a => {
  const disabled_msg = a.checkDisabled?.(e);
  return <Button {...a} title={disabled_msg || ''} disabled={!!disabled_msg} onClick={() => a.onTrigger(e)} />;
})} children={<List.Item.Meta avatar={avatarFor(e)} title={nameFor(e)} description={descFor(e)} />} />} data-sentry-element="List" data-sentry-component="displayEntities" data-sentry-source-file="EntityPicker.jsx" />;
export const EntityList = ({
  removeable,
  style
}) => {
  const {
    data
  } = useContext(AssignmentsContext);
  return <List className={styles["entitylist-wrapper"]} itemLayout="horizontal" dataSource={data.entities} style={style} renderItem={e => <List.Item actions={!removeable ? [] : [<Button type="link" icon={<DeleteTwoTone twoToneColor="#EB2F96" />} onClick={() => {
    data.entities = data.entities.filter(item => item !== e);
  }} />]} children={<List.Item.Meta avatar={avatarFor(e)} title={nameFor(e)} description={descFor(e)} />} />} data-sentry-element="List" data-sentry-component="EntityList" data-sentry-source-file="EntityPicker.jsx" />;
};
const MyStates = ({
  search,
  actions
}) => {
  const {
    loading,
    error,
    data
  } = useQuery(gql`
		query UI_MY_STATES {
			states {
				id
				name
			}
		}
	`);
  const states = useMemo(() => data?.states?.map(e => ({
    ...e,
    subEntities: 'districts'
  })), [data]);
  if (loading) {
    return <Spin size="large" />;
  }
  if (error) {
    return <ErrorMessage message="Error loading data" />;
  }
  return displayEntities(states.filter(s => s.name.toLowerCase().includes(search)), actions);
};
const MySubEntities = ({
  entity,
  search,
  actions
}) => {
  const nextSubentities = {
    District: 'schools',
    School: 'classes',
    Class: 'students'
  };
  const {
    loading,
    error,
    data
  } = useQuery(gql`
			query UI_MY_${entity.__typename.toUpperCase()}($id: ID!) {
				${entity.__typename.toLowerCase()}ById(id: $id) {
					id
					${entity.subEntities} {
						id
						name
						${entity.subEntities === 'schools' ? 'type' : ''}
						${entity.subEntities === 'classes' ? 'type, semester, grade, current, avatarImageUrl, avatarBorderColor, avatarBackgroundColor' : ''}
					}
				}
			}
		`, {
    variables: {
      id: entity.id
    }
  });
  if (loading) {
    return <Spin size="large" />;
  }
  if (error) {
    return <ErrorMessage message="Error loading data" />;
  }
  const entities = data[`${entity.__typename.toLowerCase()}ById`]?.[entity.subEntities]?.filter(e => entity.subEntities === 'classes' ? e.current : entity.subEntities === 'schools' ? entity.type === e.type : true)?.filter(e => e.name.toLowerCase().includes(search))?.flatMap(e => entity.subEntities !== 'districts' ? [e] : [{
    ...e,
    type: 'DISTRICT'
  }, {
    ...e,
    type: 'PARTNER'
  }, {
    ...e,
    type: 'COMMUNITY'
  }])?.map(e => ({
    ...e,
    subEntities: nextSubentities[e.__typename]
  }));
  return displayEntities(entities ?? [], actions);
};
const EntityPicker = () => {
  const {
    data
  } = useContext(AssignmentsContext);
  const menuConfig = useReportsAccess().menu;
  const [search, setSearch] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([{
    ...menuConfig.topLevel,
    __typename: menuConfig.topLevel.type
  }]);
  const location = breadcrumbs[breadcrumbs.length - 1];
  const addBreadcrumb = obj => {
    setSearch('');
    setBreadcrumbs([...breadcrumbs, obj]);
  };
  const reduceBreadcrumbs = obj => {
    setSearch('');
    setBreadcrumbs(breadcrumbs.slice(0, breadcrumbs.indexOf(obj) + 1));
  };
  const actions = [{
    type: 'link',
    children: 'Show',
    icon: <EyeTwoTone />,
    onTrigger: addBreadcrumb
  }, {
    type: 'link',
    children: '+ Add',
    className: styles['add-btn'],
    onTrigger: e => {
      data.entities = [...data.entities, e].sort(mixedEntitiesSorter);
    },
    checkDisabled: e => {
      if (data.entities.length > 0) {
        const included = data.entities.find(entity => entity.id === e.id && entity.__typename === e.__typename && entity.type === e.type);
        if (included) {
          return `${e.name} is already added`;
        }
        if (data.entities[0].__typename !== e.__typename) {
          return 'You can add only one type of entities';
        }
        if (data.entities[0].type !== e.type) {
          return 'You can\'t mix District Schools, Partner Schools and Community together';
        }
        if (data.entities[0].type === 'COMMUNITY' !== (e.type === 'COMMUNITY')) {
          return 'You can\'t mix community with non-community';
        }
      }
      return false;
    }
  }];
  const showGradeFilter = data.entities[0] && ['DISTRICT', 'PARTNER'].includes(data.entities[0].type);
  const showAgeFilter = data.entities[0] && data.entities[0].type === 'COMMUNITY';
  return <div className={styles["entitypicker-wrapper"]} data-sentry-component="EntityPicker" data-sentry-source-file="EntityPicker.jsx">
			<div>
				<div className="leaderboard-breadcrumbs" style={{
        display: 'block'
      }}>
					<Breadcrumb data-sentry-element="Breadcrumb" data-sentry-source-file="EntityPicker.jsx">
					{breadcrumbs.map((item, index) => <Breadcrumb.Item key={`item-${index}`}>
								<Button type="link" onClick={() => reduceBreadcrumbs(item)}>
									{item.name}
								</Button>
							</Breadcrumb.Item>)}
					</Breadcrumb>
					<Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${location?.subEntities ?? 'states'}`} suffix={<SearchOutlined />} data-sentry-element="Input" data-sentry-source-file="EntityPicker.jsx" />
				</div>
				<Card bordered={false} style={{
        boxShadow: 'none'
      }} data-sentry-element="Card" data-sentry-source-file="EntityPicker.jsx">
					{location.subEntities ? <MySubEntities entity={location} search={search.toLowerCase().trim()} actions={actions} /> : <MyStates search={search.toLowerCase().trim()} actions={actions.slice(0, 1)} />}
				</Card>
			</div>
			<Card className={styles["picked-entities-list"]} title={<>
						<div>Assign to Students in:</div>
						<div className={styles["multi-dropdowns"]}>
							{showGradeFilter && <LabeledInput label="Grade Levels">
										<GradePicker mode="multi" includeOverall={false} selected={data.grades} onChange={grades => {
            data.grades = grades;
          }} />
									</LabeledInput>}
							{showAgeFilter && <LabeledInput label="Ages">
										<AgeGroupPicker mode="multi" includeOverall={false} selected={data.ages} onChange={ages => {
            data.ages = ages;
          }} />
									</LabeledInput>}
						</div>
					</>} data-sentry-element="Card" data-sentry-source-file="EntityPicker.jsx">
				<EntityList removeable style={{
        maxHeight: '300px',
        overflow: 'auto'
      }} data-sentry-element="EntityList" data-sentry-source-file="EntityPicker.jsx" />
			</Card>
		</div>;
};
export default EntityPicker;